<template>
  <div v-if="target" id="target-property" class="bl-target-details-panel" :style="{ height: !normalizedTaskTarget ? 'calc(100% - 3rem)' : (targetTab === 'details' ? 'auto' : 'calc(100% - 3rem)') }">
    <div class="bl-target-details-tabs">
      <div
        class="bl-target-details-tab"
        :class="targetTab === 'details' ? 'bl-target-details-tab-active' : ''"
        @click="targetTab = 'details'"
      >
        <BlTextSubtitle2 text="Details & settings" />
      </div>
      <div
        class="bl-target-details-tab"
        :class="targetTab === 'progress' ? 'bl-target-details-tab-active' : ''"
        @click="targetTab = 'progress'"
      >
        <BlTextSubtitle2 text="Progress & records" />
        <BlBubbleCount
          v-if="!readonly"
          :count="entries.filter(i => !i.type.includes('PhaseLine')).length"
        />
      </div>
      <div
        v-if="target.type === 'Target::Sbt' && !normalizedTaskTarget"
        class="bl-target-details-tab"
        :class="
          targetTab === 'problem_behaviors'
            ? 'bl-target-details-tab-active'
            : ''
        "
        @click="targetTab = 'problem_behaviors'"
      >
        <BlTextSubtitle2 text="Problem behaviors" />
        <BlBubbleCount
          v-if="!readonly"
          :count="entries.filter(i => !i.type.includes('PhaseLine')).length"
        />
      </div>
    </div>
    <div
      v-if="showRecommendation"
      class="sticky top-0 flex items-center justify-between gap-2 px-6 py-2 bg-orange-200"
    >
      <div class="flex items-center gap-2">
        <div class="p-2 bg-orange-300" :style="{ borderRadius: '4px' }">
          <BlIcon icon="tooltip" class="w-6 h-6" />
        </div>
        <div
          v-if="ShowCloseRecommendationText"
          class="flex flex-wrap items-center gap-1"
        >
          <BlTextBody2
            class="text-orange-800"
            text="You can still view recommendations in the"
          />
          <BlTextBody2
            class="font-bold text-orange-800"
            text="Recommendations"
          />
          <BlTextBody2 class="text-orange-800" text="tab." />
        </div>
        <div v-else class="flex flex-wrap items-center gap-1">
          <BlTextBody2 class="text-orange-800" text="This target has" />
          <BlTextBody2
            class="font-bold text-orange-800"
            :text="`${target.total_success} successful session(s)`"
          />
          <BlTextBody2 class="text-orange-800" text="with" />
          <BlTextBody2
            class="font-bold text-orange-800"
            :text="`${target.consecutive_success} consecutive success.`"
          />
          <BlTextBody2
            class="text-orange-800"
            text="Do you want to update the status to"
          />
          <BlTextBody2
            class="font-bold text-orange-800"
            :text="`'Mastered'?`"
          />
        </div>
      </div>
      <div v-if="!ShowCloseRecommendationText" class="flex items-center gap-2">
        <BlInputButton
          label="Accept"
          type="orange"
          dense
          :loading="acceptLoading"
          @clicked="onAcceptRecommendation"
        />
        <BlIcon
          icon="close"
          color="orange-6"
          clickable
          class="flex-shrink-0 w-6 h-6"
          @clicked="onCloseRecommendation"
        />
      </div>
    </div>
    <TargetPropertyDetails
      v-if="targetTab === 'details' &&  !normalizedTaskTarget"
      :target="target"
      :readonly="readonly"
    />

    <TaskPropertyDetails
      v-if="targetTab === 'details' && normalizedTaskTarget"
      :task="taskTarget"
      :target="target"
      :readonly="readonly"
    />

    <TargetPropertyProgress
      v-if="targetTab === 'progress'"
      :target="target"
      :entries="entries"
      :taskTarget.sync="normalizedTaskTarget"
      :graphEntries="graphEntries"
      @updateTaskTarget="updateTaskTarget"
      :readonly="readonly"
      @select-task="selectTask"
    />
    <TargetProblemBehaviors
      v-if="targetTab === 'problem_behaviors'"
      :target="target"
      :entries="entries"
      :graphEntries="graphEntries"
      :readonly="readonly"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";

import TargetPropertyDetails from "./PropertyDetails.vue";
import TargetPropertyProgress from "./PropertyProgress.vue";
import TargetProblemBehaviors from "./PropertyProblemBehaviors.vue";

import BlBubbleCount from "../../BlBubbleCount.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlIcon from "../../BlIcon.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import TaskPropertyDetails from "./TaskPropertyDetails.vue";

export default {
  name: "TargetProperty",
  components: {
    TargetPropertyDetails,
    TargetPropertyProgress,
    TaskPropertyDetails,
    TargetProblemBehaviors,
    BlBubbleCount,
    BlTextSubtitle2,
    BlIcon,
    BlTextBody2,
    BlInputButton,
    BlTextBody1
  },
  props: {
    target: {
      type: Object,
      required: true
    },
    graphEntries: {
      type: Object,
      default: () => ({ labels: [], datasets: [], recorded_by: [] })
    },
    entries: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    },
    taskTarget: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      targetTab: "details",
      acceptLoading: false,
      ShowCloseRecommendationText: false,
      showRecommendation: false
    };
  },
  computed: {
    currentActionRecommendation() {
      const arr = this.target.action_recommendations || [];
      return arr.length ? arr[0] : { id: null };
    },
    hasPassedActionRecommendation() {
      const arr = this.target.action_recommendations || [];
      const idx = arr.findIndex(i => i.visible && i.passed);
      return idx > -1;
    },
    normalizedTaskTarget() {
      return this.taskTarget && Object.keys(this.taskTarget).length > 0 ? this.taskTarget : null;
    }
  },
  watch: {
    target: {
      deep: true,
      handler(val) {
        if (this.targetTab === "problem_behaviors" && val.type !== "Target::Sbt") this.targetTab = "progress";
        this.checkRecommendationAvailability(val);
      }
    },
    taskTarget: {
      deep: true,
      handler(val) {
        if (this.targetTab === "problem_behaviors" && val.type !== "Target::Sbt") this.targetTab = "progress";
      }
    }
  },
  mounted() {
    this.checkRecommendationAvailability(this.target);
  },
  methods: {
    ...mapActions(["acceptActionRecommendation", "showToast"]),
    checkPassedActionRecommendation(target) {
      const arr = target.action_recommendations || [];
      const idx = arr.findIndex(i => i.visible && i.passed);
      return idx > -1;
    },

    selectTask(task) {
      this.$emit("select-task", task);
    },
    updateTaskTarget(val) {
      this.$emit("updateTaskTarget", val);
    },
    checkRecommendationAvailability(val) {
      if (
        this.checkPassedActionRecommendation(val) &&
        val.status !== "mastered"
      ) {
        this.showRecommendation = true;
      } else {
        this.showRecommendation = false;
      }
    },
    onCloseRecommendation() {
      this.ShowCloseRecommendationText = true;
      setTimeout(() => {
        this.showRecommendation = false;
        this.ShowCloseRecommendationText = false;
      }, 7500);
    },
    onAcceptRecommendation() {
      this.acceptLoading = true;
      this.acceptActionRecommendation({
        target_id: this.target.id,
        ids: JSON.stringify(this.currentActionRecommendation.id)
      }).then(({ success }) => {
        this.acceptLoading = false;
        if (!success) return;
        const title = "Target changed to Mastered";
        const message = `Success! The target <b>"${this.target.name}"</b> has been <br /> marked as <b>Mastered.</b>`;
        this.showToast({ title, message, type: "success" });
        this.showRecommendation = false;
      });
    }
  }
};
</script>

<style scoped lang="scss">
.bl-target-details-panel {
  position: relative;
  width: calc(100% - 3rem);
  margin: 1.5rem;
  border-radius: 1.25rem;
  border: 1px solid var(--border-color);
  box-sizing: border-box;
  background: var(--background-depth-one-color);
  display: flex;
  flex-direction: column;
  padding-bottom: 2rem;
  .bl-target-details-tabs {
    position: relative;
    width: 100%;
    padding: 1.5rem 1.5rem 0 1.5rem;
    border-bottom: 1px solid var(--border-color);
    box-sizing: border-box;
    display: flex;
    .bl-target-details-tab {
      cursor: pointer;
      position: relative;
      width: 50%;
      height: 2rem;
      padding-bottom: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      &.bl-target-details-tab-active::after {
        content: "";
        position: absolute;
        left: 0;
        bottom: -1px;
        width: 100%;
        height: 0.25rem;
        background-color: var(--primary-color);
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/clients/target/Property.vue